<script lang="ts">
    import { run } from 'svelte/legacy'

    import { createEventDispatcher, getContext } from 'svelte'
    import { flip } from 'svelte/animate'

    import Icon from '@iconify/svelte'
    import { v4 as uuidv4 } from 'uuid'

    import type { HydriaDocument } from 'hydria/HydriaDocument'
    import { createDragHandle } from 'hydria/external/DragHandleZone'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'
    import { gettext as _, tgettext } from 'hydria/services/gettext'

    import Alignment from '../BaseFields/Alignment.svelte'
    import HtmlField from '../BaseFields/HtmlField.svelte'
    import SchemeSelection from '../BaseFields/Scheme.svelte'
    import VerticalFieldWidget from '../Widgets/VerticalFieldWidget.svelte'
    import type { ITextColumnsEntry, ITextColumnsSection } from '../models'
    import { asAny } from 'hydria/svelte_helpers'

    interface Props {
        sectionEdit: ITextColumnsSection
        sectionSaved: ITextColumnsSection
        disabled: boolean
    }

    let { sectionEdit = $bindable(), sectionSaved, disabled }: Props = $props()

    const dispatch = createEventDispatcher()

    const Document = getContext('Document') as HydriaDocument

    // ****************************************************************************  Styles / Schemes
    function findStyle(source: string[], start: string) {
        const first = source.filter((s) => s.startsWith(`${start}-`))[0]
        return first != null ? first.slice(start.length + 1) : null
    }
    function setStyle(start: string, value: string) {
        const data = { styles: [...sectionEdit.styles.filter((s) => !s.startsWith(`${start}-`)), `${start}-${value}`] }
        data.styles.sort()
        dispatch('set', data)
    }

    let alignClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'align') : null)
    let savedAlignClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'align') : null)

    let schemeClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'scheme') : null)
    let savedSchemeClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'scheme') : null)

    let columnClass: string | null = $derived(sectionEdit != null ? findStyle(sectionEdit.styles, 'column') : null)
    let savedColumnClass: string | null = $derived(sectionSaved != null ? findStyle(sectionSaved.styles, 'column') : null)

    // ****************************************************************************  Ajout / Enlever

    function addText() {
        const id = uuidv4()
        const lentries = [
            ...sectionEdit.entries,
            {
                id: id,
                content_html: '<h1>' + id + '</h1>',
            },
        ]
        dispatch('set', { entries: lentries })
    }
    function removeText(entry: ITextColumnsEntry) {
        dispatch('set', { entries: sectionEdit.entries.filter((e) => e.id !== entry.id) })
    }

    // !null = entrée en édition dans un modal
    // **IMPORTANT** Ceci est une *COPIE*
    let entryInEdit: ITextColumnsEntry | undefined = $state()

    // ****************************************************************************  Sort
    const flipDurationMs = 250

    function handleSort(e: any) {
        sectionEdit.entries = e.detail.items
    }

    function handleSortEnd(e: any) {
        sectionEdit.entries = e.detail.items
        dispatch('set', { entries: sectionEdit.entries })
    }

    const { dragHandleZone, dragHandle } = createDragHandle()

    // ************************************************************************ Mini preview
    // FIXME: probably not working because is now a proxy ($state)
    let selectedEntry: ITextColumnsEntry | null = $state(null)
    run(() => {
        if (selectedEntry == null || sectionEdit.entries.indexOf(selectedEntry) < 0) {
            // Entry was deleted..
            selectedEntry = sectionEdit.entries[0] // null if empty or first
        }
    })

    function extractTitle(html: string) {
        if (html) {
            const parser = new DOMParser()
            const hdoc = parser.parseFromString(html, 'text/html')
            const h1s = hdoc.getElementsByTagName('h1')
            if (h1s && h1s.length) return h1s[0].textContent
        }
        return '(' + $_('sans titre') + ')'
    }
</script>

<div class="container mb-3">
    <div class="row">
        <div class="col" style="width: 14rem;">
            <SchemeSelection {disabled} value={schemeClass} saved={savedSchemeClass} save={(e) => setStyle('scheme', e)} />
        </div>
        <div class="col" style="width: 11rem;">
            <Alignment {disabled} value={alignClass} saved={savedAlignClass} save={(e) => setStyle('align', e)} />
        </div>
        <div class="col" style="width: 11rem;">
            <Alignment
                {disabled}
                label={tgettext('Style')}
                value={columnClass}
                saved={savedColumnClass}
                save={(e) => setStyle('column', e)}
                grouped={false}
                aligns={[
                    { id: 'normal', text: tgettext('Normal'), icon: null, iconText: 'Normal' },
                    { id: 'border', text: tgettext('Avec contour'), icon: null, iconText: 'Contour' },
                    { id: 'border-logo', text: tgettext('Avec contour et logo en fond'), icon: null, iconText: 'Contour + Logo' },
                ]}
            />
        </div>
        <div class="col">
            <VerticalFieldWidget label={tgettext('Prévisualisation')} isValid={true} isPristine={true}>
                <div class="section-text-columns overflow-hidden outer section-quote scheme {sectionEdit.styles.join(' ')}">
                    <div class="inner">
                        <div class="content container-xl">
                            <div class="row">
                                <div class="column-layout">
                                    {#if sectionEdit.intro_html}
                                        <div class="cl-intro">{@html sectionEdit.intro_html}</div>
                                    {/if}
                                    <div class="cl-columns cl-{sectionEdit.entries.length}-columns">
                                        {#each sectionEdit.entries as entry (entry.id)}
                                            <div class="cl-column">{@html entry.content_html}</div>
                                        {/each}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </VerticalFieldWidget>
        </div>
        <div class="col" style="max-width: 12rem;">
            <VerticalFieldWidget label={tgettext('Ajouter un texte')} isValid={true} isPristine={true}>
                <button type="button" class="btn btn-primary" {disabled} onclick={() => addText()}><Icon inline icon="mdi:playlist-plus" /> {$_('Ajouter un texte')}</button>
            </VerticalFieldWidget>
        </div>
    </div>
</div>

{#if sectionEdit}
    <div class="card mb-2">
        <div class="card-header text-bg-secondary">{$_('Texte d’introduction')}</div>
        <div class="card-body text-bg-light">
            <div class="form-text">{$_('Ce texte optionnel est affiché en prélude à cette section.')}</div>
        </div>
        <div class="card-body text-bg-light">
            <IntroHtml
                saved={sectionSaved ? sectionSaved.intro_html : undefined}
                value={sectionEdit.intro_html}
                showLabel={false}
                {disabled}
                helpText={undefined}
                isValid={(v) => {
                    return true
                }}
                save={(e) => {
                    const clean = e.replaceAll(/<p><\/p>/gi, '')
                    dispatch('set', { intro_html: clean ? clean : null })
                }}
            />
        </div>
    </div>
{/if}

<div class="entries d-grid gap-3" use:dragHandleZone={{ items: sectionEdit.entries, flipDurationMs: flipDurationMs, type: 'text-columns' }} onconsider={handleSort} onfinalize={handleSortEnd}>
    {#each sectionEdit.entries as entry (entry.id)}
        <div class="card" animate:flip={{ duration: flipDurationMs }}>
            <div class="card-header text-bg-secondary d-flex align-items-center gap-2">
                <div><span use:dragHandle class="btn btn-sm btn-outline-light"><Icon inline icon="mdi:drag-vertical" /></span></div>
                <div class="col">
                    <span>{extractTitle(entry.content_html)}</span>
                </div>
                <div><button type="button" class="btn btn-sm btn-light" {disabled} onclick={() => (entryInEdit = { ...entry })}><Icon inline icon="mdi:pencil" /></button></div>
                <div><button type="button" class="btn btn-sm btn-danger" {disabled} onclick={() => removeText(entry)}><Icon inline icon="mdi:trash" /></button></div>
            </div>
            <div class="card-body text-bg-light">
                <div class="entry">
                    <div
                        class="content"
                        aria-hidden="true"
                        ondblclick={() => {
                            if (!disabled) entryInEdit = { ...entry }
                        }}
                    >
                        {@html entry.content_html}
                    </div>
                </div>
            </div>
        </div>
    {/each}
</div>

{#if entryInEdit != null}
    <HtmlField
        value={entryInEdit.content_html}
        saved={sectionSaved == null ? null : sectionSaved[entryInEdit.content_html]}
        label={tgettext('Texte de la colonne')}
        helpText={tgettext('Ne pas oublier d’ajouter une entête de 1er niveau pour le titre.')}
        isValid={(v) => !!(v && v.trim())}
        {Document}
        on:close={() => (entryInEdit = null)}
        on:save={(e) => {
            const entry = sectionEdit.entries.filter((i) => i.id === asAny(entryInEdit).id)[0]
            if (entry) {
                entry.content_html = e.detail
                dispatch('set', { entries: sectionEdit.entries })
            }
            entryInEdit = null
        }}
    />
{/if}
